import React from 'react';
import { Progress, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    Textarea,
    toasty,
} from '../common/forms/FormElements';
import { Modal } from '../common/Modal';
import { FlagTypeOptions } from './DispatchIssuesConstants';

const IsResolvedOptions = [
    { value: 'false', label: 'Unresolved' },
    { value: 'true', label: 'Resolved' },
];

class DispatchIssuesIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            rowsSelected: [],
        };

        this.onRowSelected = this.onRowSelected.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onRowSelected(e) {
        const rs = e.api.getSelectedRows();
        this.setState({ rowsSelected: rs });
    }

    onResolveClick = () => {
        const { rowsSelected, gridOptions } = this.state;
        if (!rowsSelected.length) return;

        const [selected] = rowsSelected;
        if (selected.isResolved) return;

        Modal.show({
            title: <>
                <FontAwesomeIcon
                    icon={faFlag}
                    className="mr-2 text-success"
                />
                Resolve Flag
            </>,
            initialValues: { resolutionNotes: '' },
            SaveButtonProps: { label: 'Resolve', color: 'success' },
            onSubmit: async ({ resolutionNotes }) => {
                const response = await httpClient.post(
                    ApiRoutes.dispatchFlag.resolve(selected.id),
                    { resolutionNotes: resolutionNotes || null },
                    httpClient.format.none,
                );

                if (!response.ok || response.redirected) {
                    throw new Error('Resolve failed');
                }

                toasty.success('Flag resolved successfully.');
                this.setState({ rowsSelected: [] });
                gridOptions.refresh();
            },
            body: () => (
                <Textarea
                    id="resolutionNotes"
                    name="resolutionNotes"
                    label={(
                        <>
                            Resolution Notes
                        </>
                    )}
                    placeholder="Enter optional notes regarding how this issue was resolved..."
                    rows={4}
                />
            ),
        });
    };

    populateState = async () => {
        const dispatchLocations = await httpClient.js(ApiRoutes.typeAheads.dispatchLocations());

        const gridOptions = createGridOptions(this);
        const context = this;

        gridOptions.rowSelection = 'single';
        gridOptions.onRowSelected = this.onRowSelected;
        gridOptions.rowClassRules = {};

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const flagTypeFilterParams = {
            suppressFilterButton: true,
            options: FlagTypeOptions,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const isResolvedFilterParams = {
            suppressFilterButton: true,
            options: IsResolvedOptions,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'FlagDate',
                headerName: 'Date',
                field: 'flagDate',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'desc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'Job.Contract.Company.CompanyName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: { suppressFilterButton: true },
            },
            {
                colId: 'Job.JobNumber',
                headerName: 'Job #',
                field: 'jobNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: { suppressFilterButton: true },
            },
            {
                colId: 'DispatchFlagTypeId',
                headerName: 'Flag Type',
                field: 'flagType',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: flagTypeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: flagTypeFilterParams,
            },
            {
                colId: 'Job.DispatchOffice.CompanyName',
                headerName: 'Dispatching',
                field: 'dispatchOfficeName',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: dispatchFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: dispatchFilterParams,
            },
            {
                colId: 'Notes',
                headerName: 'Notes',
                field: 'notes',
                sortable: false,
                flex: 2.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: { suppressFilterButton: true },
            },
            {
                colId: 'ResolutionNotes',
                headerName: 'Resolution Notes',
                field: 'resolutionNotes',
                sortable: false,
                flex: 2.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: { suppressFilterButton: true },
            },
            {
                colId: 'CreatedByUser.LastName',
                headerName: 'Flagged By',
                field: 'flaggedBy',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: { suppressFilterButton: true },
            },
            {
                colId: 'CreatedOn',
                headerName: 'Flagged On',
                field: 'flaggedOn',
                sortable: true,
                flex: 1.5,
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'IsResolved',
                headerName: 'Status',
                field: 'isResolved',
                sortable: true,
                flex: 1.5,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: isResolvedFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: isResolvedFilterParams,
                valueFormatter: ({ value }) => (value ? 'Resolved' : 'Unresolved'),
            },
        ];

        gridOptions.onFilterChanged = () => { };
        gridOptions.onSortChanged = () => { };
        gridOptions.onFirstDataRendered = () => { };

        gridOptions.onGridReady = (params) => {
            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        const dataSource = createDataSource(
            ApiRoutes.dispatchFlag.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    };

    render() {
        const {
            gridOptions,
            loading,
            rowsSelected,
        } = this.state;

        if (loading) {
            return <Progress />;
        }

        const hasSelection = rowsSelected.length > 0;
        const selectedFlag = hasSelection ? rowsSelected[0] : null;
        const canResolveSelectedFlag = hasSelection && !selectedFlag.isResolved;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faFlag}
                        className="mr-2 text-danger"
                    />
                    <span>Dispatch Issues</span>
                </PageHeading>

                <DataGridToolbar
                    entity="DispatchIssues"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                >
                    <Button
                        color="danger"
                        disabled={!canResolveSelectedFlag}
                        onClick={this.onResolveClick}
                        className="ml-2"
                    >
                        Resolve Flag
                    </Button>
                </DataGridToolbar>

                <DataGrid
                    domLayout="normal"
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}

export default DispatchIssuesIndex;
